import { authFetch, getSessionToken } from "site/services/auth-session.service";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

export type EditorBookData = {
  title?: string;
  title_en?: string;
  title_fr?: string;
  cost?: number;
  prev_cost?: number;
  promo?: string;
};

export type EditorBookExerciseSummary = {
  id: number;
  title_arabic?: string;
  title_russian?: string;
  title_en?: string;
  title_fr?: string;
  description?: string;
  type?: number;
  length?: number;
  is_free?: boolean;
};

export type EditorBookListItem = {
  id: number;
  data: EditorBookData;
  texts: number[];
  is_published: boolean;
  created_at?: string;
};

export type EditorBookPayload = EditorBookListItem & {
  styles?: string;
  exercises?: EditorBookExerciseSummary[];
};

export type EditorBookSaveBody = {
  data: EditorBookData;
  texts: number[];
  is_published: boolean;
  styles?: string;
};

function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function editorBase(): string {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  return base;
}

export async function fetchEditorBooks(): Promise<EditorBookListItem[]> {
  const resp = await authFetch(`${editorBase()}/editor/books`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!resp.ok) {
    throw new Error(`editor list books: HTTP ${resp.status}`);
  }
  const json = (await resp.json()) as EditorBookListItem[];
  return Array.isArray(json) ? json : [];
}

export async function fetchEditorBook(bookId: number | string): Promise<EditorBookPayload> {
  const resp = await authFetch(`${editorBase()}/editor/books/${encodeURIComponent(String(bookId))}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!resp.ok) {
    throw new Error(`editor get book: HTTP ${resp.status}`);
  }
  const json = (await resp.json()) as EditorBookPayload;
  return {
    ...json,
    id: Number(json.id),
    data: json.data && typeof json.data === "object" ? json.data : {},
    texts: Array.isArray(json.texts) ? json.texts.map(Number) : [],
    exercises: Array.isArray(json.exercises) ? json.exercises : [],
  };
}

export async function saveEditorBook(bookId: number | string, body: EditorBookSaveBody): Promise<void> {
  const resp = await authFetch(`${editorBase()}/editor/books/${encodeURIComponent(String(bookId))}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`editor save book: HTTP ${resp.status}`);
  }
}

export async function createEditorBook(id?: number): Promise<number> {
  const resp = await authFetch(`${editorBase()}/editor/books`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(id && id > 0 ? { id } : {}),
  });
  if (!resp.ok) {
    throw new Error(`editor create book: HTTP ${resp.status}`);
  }
  const json = (await resp.json()) as { id?: number };
  const bookId = Number(json.id);
  if (!Number.isFinite(bookId) || bookId <= 0) {
    throw new Error("editor create book: invalid id");
  }
  return bookId;
}

export async function createEditorBookText(bookId: number | string, id?: number): Promise<number> {
  const resp = await authFetch(
    `${editorBase()}/editor/books/${encodeURIComponent(String(bookId))}/texts`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(id && id > 0 ? { id } : {}),
    },
  );
  if (!resp.ok) {
    throw new Error(`editor create book text: HTTP ${resp.status}`);
  }
  const json = (await resp.json()) as { id?: number };
  const textId = Number(json.id);
  if (!Number.isFinite(textId) || textId <= 0) {
    throw new Error("editor create book text: invalid id");
  }
  return textId;
}
